import React, { useCallback, useEffect, useMemo, useState } from "react";
import useStore from "../../../useStore";
import { useAuthentication } from "../../../Hooks/useAuthentication";
import Loader from "../../Elements/Loader/Loader";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import "./getForms.css";
import Calendar from "../../Layout/Calendar/Calendar";
import BuscadorForms from "../../Layout/Buscador/BuscadorYFiltro";

const isProcesoOtro = (f) => {
  const p = String(f?.proceso || "").trim().toUpperCase();
  const po = String(f?.procesoOtro || "").trim();
  return p === "OTRO" || po.length > 0;
};

const dateToISO = (date) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const toISOFromForm = (f) => {
  const fecha = String(f?.fecha || "").trim();

  if (!fecha) return f?.createdAt ? String(f.createdAt).slice(0, 10) : "";

  // si ya viene en formato yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    return fecha;
  }

  // si viene como dd-mm-yy o dd-mm-yyyy
  const parts = fecha.split("-");
  if (parts.length === 3) {
    const [dd, mm, yy] = parts;
    const yyyy = yy.length === 2 ? `20${yy}` : yy;
    return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  }

  return f?.createdAt ? String(f.createdAt).slice(0, 10) : "";
};

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const startOfWeekMonday = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=domingo, 1=lunes...
  const diff = day === 0 ? -6 : 1 - day; // si domingo, retrocede 6
  d.setDate(d.getDate() + diff);
  return d;
};

const tipoFolioText = (tipo) => {
  switch (String(tipo || "").trim().toUpperCase()) {
    case "SIMPLE":
      return "folios simples";
    case "DOBLE":
      return "folios dobles";
    case "TRIPLE":
      return "folios triples";
    default:
      return "folios";
  }
};

const endOfWeekSunday = (date) => {
  const d = startOfWeekMonday(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

const startOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfMonth = (date) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
};

const weekRangeISO = (date) => {
  const start = startOfWeekMonday(date);
  const end = endOfWeekSunday(date);
  return { startISO: dateToISO(start), endISO: dateToISO(end) };
};

const monthRangeISO = (date) => {
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  return { startISO: dateToISO(start), endISO: dateToISO(end) };
};

const GetForms = () => {
  const showToast = useToast();
  const { isAuthenticated, role } = useAuthentication();
  const { fetchDataGetForms, forms, loading, error } = useStore();

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);

  const normalizedRole = (role || "").toString().trim().toLowerCase();
  const canViewForms = ["administrador", "administrador-t1", "administrador-t2", "supervisor"].includes(
    normalizedRole
  );

  const [selectedDateObj, setSelectedDateObj] = useState(startOfToday());
  const selectedDate = useMemo(() => dateToISO(selectedDateObj), [selectedDateObj]);

  const load = useCallback(async () => {
    try {
      await fetchDataGetForms();
    } catch (e) { }
  }, [fetchDataGetForms]);

  useEffect(() => {
    if (isAuthenticated && canViewForms) load();
  }, [isAuthenticated, canViewForms, load]);

  useEffect(() => {
    if (error) {
      const msg = error instanceof Error ? error.message : error?.error || "Error desconocido";
      showToast("error", msg);
    }
  }, [error, showToast]);

  const filtered = useMemo(() => {
    const list = Array.isArray(forms) ? forms : [];

    return list.filter((f) => {
      const iso = toISOFromForm(f);
      return iso === selectedDate;
    });
  }, [forms, selectedDate]);

  const dataForTotals = useMemo(() => {
    if (!search.trim()) return filtered;
    const idsDelDia = new Set((filtered || []).map((f) => f.FormId));
    return (Array.isArray(results) ? results : []).filter((f) => idsDelDia.has(f.FormId));
  }, [search, results, filtered]);

  const totalDia = useMemo(() => {
    return (Array.isArray(dataForTotals) ? dataForTotals : []).reduce((acc, f) => {
      if (isProcesoOtro(f)) return acc; // 👈 NO contar OTRO
      return acc + toInt(f?.totalEntregadas);
    }, 0);
  }, [dataForTotals]);

  const baseForRangeTotals = useMemo(() => {
    return search.trim()
      ? (Array.isArray(results) ? results : [])
      : (Array.isArray(forms) ? forms : []);
  }, [search, results, forms]);

  const totalSemana = useMemo(() => {
    const list = baseForRangeTotals;
    const { startISO, endISO } = weekRangeISO(selectedDateObj);

    return list.reduce((acc, f) => {
      if (isProcesoOtro(f)) return acc; // 👈 NO contar OTRO

      const iso = toISOFromForm(f);
      if (!iso) return acc;

      if (iso >= startISO && iso <= endISO) return acc + toInt(f?.totalEntregadas);
      return acc;
    }, 0);
  }, [baseForRangeTotals, selectedDateObj]);

  const totalMes = useMemo(() => {
    const list = baseForRangeTotals;
    const { startISO, endISO } = monthRangeISO(selectedDateObj);

    return list.reduce((acc, f) => {
      if (isProcesoOtro(f)) return acc; // 👈 NO contar OTRO

      const iso = toISOFromForm(f);
      if (!iso) return acc;

      if (iso >= startISO && iso <= endISO) return acc + toInt(f?.totalEntregadas);
      return acc;
    }, 0);
  }, [baseForRangeTotals, selectedDateObj]);

  const listToShow = useMemo(() => {
    if (!search.trim()) return filtered;
    const idsDelDia = new Set((filtered || []).map((f) => f.FormId));
    return (Array.isArray(results) ? results : []).filter((f) => idsDelDia.has(f.FormId));
  }, [search, results, filtered]);

  if (!isAuthenticated) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning mb-0">No has iniciado sesión.</div>
      </div>
    );
  }

  if (!canViewForms) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger mb-0">No tienes permisos para ver formularios.</div>
      </div>
    );
  }

  if (loading) return <Loader />;

  return (
    <div className="gf-page">
      <div className="container py-4">
        {/* Header */}
        <div className="gf-header">
          <div>
            <h2 className="gf-title">Formularios</h2>
            <div className="gf-subtitle">
              Selecciona una fecha en el calendario y te muestro los formularios de ese día.
            </div>
          </div>

          <button className="btn gf-refresh-btn" onClick={load}>
            Refrescar
          </button>
        </div>

        {/* ✅ Totales */}
        <div className="gf-totals">
          <div className="gf-total-card">
            <div className="gf-total-label">Total del día</div>
            <div className="gf-total-value">{totalDia}</div>
            <div className="gf-total-sub">{selectedDate}</div>
          </div>

          <div className="gf-total-card">
            <div className="gf-total-label">Total semana (L-D)</div>
            <div className="gf-total-value">{totalSemana}</div>
            <div className="gf-total-sub">Semana de la fecha seleccionada</div>
          </div>

          <div className="gf-total-card">
            <div className="gf-total-label">Total del mes</div>
            <div className="gf-total-value">{totalMes}</div>
            <div className="gf-total-sub">Mes de la fecha seleccionada</div>
          </div>
        </div>

        {/* ✅ Layout sticky + scroll */}
        <div className="row g-3 gf-layout">
          {/* Sidebar sticky (Buscador + Calendario) */}
          <div className="col-12 col-lg-4">
            <div className="gf-sticky">
              <div className="mb-3">
                <BuscadorForms
                  search={search}
                  setSearch={setSearch}
                  data={forms}
                  onResults={setResults}
                />
              </div>

              <div className="gf-card">
                <div className="gf-card-body">
                  <div className="gf-section-title">Calendario</div>

                  <Calendar
                    selectedDateObj={selectedDateObj}
                    onChangeDate={setSelectedDateObj}
                    forms={forms}
                  />

                  <div className="gf-selected mt-3">
                    Mostrando: <span className="gf-selected-date">{selectedDate}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Panel con scroll (solo data) */}
          <div className="col-12 col-lg-8">
            <div className="gf-scroll-panel">
              {filtered.length === 0 ? (
                <div className="alert alert-info gf-alert">
                  No hay formularios para <b>{selectedDate}</b>.
                </div>
              ) : listToShow.length === 0 ? (
                <div className="alert alert-warning gf-alert">
                  No hay resultados para tu búsqueda en <b>{selectedDate}</b>.
                </div>
              ) : (
                <div className="row g-3">
                  {listToShow.map((f) => (
                    <div className="col-12" key={f.FormId}>
                      <div className="gf-form-card">
                        {/* Top */}
                        <div className="gf-form-head">
                          <div className="gf-form-title">
                            OT: <b>{f.noOT || "-"}</b> · Máquina: <b>{f.maquina || "-"}</b> · Cliente:{" "}
                            <b>{f.cliente || "-"}</b>
                          </div>

                          <div className="gf-badges">
                            <span className="badge gf-badge-dark">Turno: {f.turno || "-"}</span>
                            <span className="badge gf-badge-soft">
                              Proceso:{" "}
                              {f.proceso === "OTRO" && f.procesoOtro
                                ? `OTRO - ${f.procesoOtro}`
                                : f.proceso || "-"}
                            </span>
                          </div>
                        </div>

                        <div className="gf-divider" />

                        {/* Campos seccionados */}
                        <div className="gf-sections">
                          <div className="gf-section">
                            <div className="gf-section-h">Datos generales</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">Fecha</div>
                                <div className="gf-strong">
                                  {f.fecha || (f.createdAt ? String(f.createdAt).slice(0, 10) : "-")}
                                </div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Folios del</div>
                                <div className="gf-strong">{f.foliosDel || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Al</div>
                                <div className="gf-strong">{f.al || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Logotipo</div>
                                <div className="gf-strong">{f.logotipo || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Modelo</div>
                                <div className="gf-strong">{f.modelo || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Longitud</div>
                                <div className="gf-strong">{f.longitud || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Color</div>
                                <div className="gf-strong">{f.color || "-"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Horas</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">Hora inicio</div>
                                <div className="gf-strong">{f.horaInicio || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Hora término</div>
                                <div className="gf-strong">{f.horaTermino || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Registro Creado</div>
                                <div className="gf-strong">
                                  {f.createdAt ? new Date(f.createdAt).toLocaleString() : "-"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Entregas</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">
                                  Total entregadas ({tipoFolioText(f.tipoFolio)})
                                </div>
                                <div className="gf-strong">{f.totalEntregadas ?? "-"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Mermas</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">Mermas defecto</div>
                                <div className="gf-strong">{f.mermasDefecto ?? "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Mermas error humano</div>
                                <div className="gf-strong">{f.mermasErrorHumano ?? "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Mermas recupera</div>
                                <div className="gf-strong">{f.mermasRecupera ?? "-"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Pruebas</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">Pruebas calidad</div>
                                <div className="gf-strong">{f.pruebasCalidad ?? "-"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Observaciones</div>
                            <div className="gf-grid">
                              <div className="gf-item gf-item-wide">
                                <div className="gf-muted">Observaciones</div>
                                <div className="gf-strong">{f.observaciones || "-"}</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {f.submittedBy?.username && (
                          <div className="gf-signed">
                            Firmado por: <b className="text-dark">{f.submittedBy.username}</b>
                            {f.submittedBy.role ? ` (${f.submittedBy.role})` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetForms;